import {
  Address,
  Credit,
  CustomerDetails,
  Expenditure,
  Income,
  Introduction,
  Job,
} from '@kanda-libs/ks-frontend-services';

import { ExtendedCache } from 'hooks/useGetCache';

import { EXAMPLE_ADDRESS } from '../constants';
import {
  ApplyFormCreditFieldValues,
  ApplyFormFieldValues,
  ApplyFormRestFieldValues,
  ApplyFormRestFieldValuesAddress,
  RecursivePartial,
} from '../context';
import { safeJsonParse } from '../helpers';

const formatMonthlyMortgage = (introduction: Introduction) => {
  const mortgage =
    introduction?.consumer_details?.finance_details?.expenditures?.filter(
      (expenditure: Expenditure) => expenditure.expenditure_type === 'mortgage',
    )?.[0];
  if (!mortgage) return {};
  return {
    monthly_mortgage_amount: mortgage.monthly_amount?.amount,
  };
};

const formatMajorMonthlies = (introduction: Introduction) => {
  const incomeAfterTax =
    introduction?.consumer_details?.finance_details?.incomes?.filter(
      (income: Income) => income.income_type === 'earning_after_tax',
    )?.[0];
  const creditRepayments =
    introduction?.consumer_details?.finance_details?.expenditures?.filter(
      (expenditure: Expenditure) =>
        expenditure.expenditure_type === 'credit_card',
    )?.[0];
  return {
    ...(incomeAfterTax && incomeAfterTax?.monthly_amount?.amount
      ? {
          income_after_tax: incomeAfterTax.monthly_amount?.amount || 0,
        }
      : {}),
    ...(creditRepayments && creditRepayments?.monthly_amount?.amount
      ? {
          credit_repayments: creditRepayments.monthly_amount?.amount || 0,
        }
      : {}),
  };
};

const formatAddress = (
  address: Address | undefined,
  created: Date,
): ApplyFormRestFieldValuesAddress[] => {
  if (!address) return [];
  const date = new Date(created);
  const months = address.months_at_address;
  if (!months) return [];
  const monthStart = date.getMonth();
  const monthsDiff = months % 12;
  const yearsDiff = Math.floor(months / 12);
  if (monthsDiff > monthStart) {
    const finalMonth = monthStart + (12 - monthsDiff);
    const finalYear = date.getFullYear() - yearsDiff - 1;
    return [
      {
        month_moved_in: String(finalMonth),
        year_moved_in: String(finalYear),
      },
    ];
  }
  const finalMonth = monthStart - monthsDiff;
  const finalYear = date.getFullYear() - yearsDiff;
  return [
    {
      month_moved_in: String(finalMonth),
      year_moved_in: String(finalYear),
    },
  ];
};

const formatAddresses = (
  introduction: Introduction,
): ApplyFormRestFieldValues => {
  const created = introduction?.metadata?.created_at;
  if (!created) return {};
  return {
    address: [
      ...formatAddress(
        introduction?.consumer_details?.customer_details?.current_address,
        created,
      ),
      ...formatAddress(
        introduction?.consumer_details?.customer_details?.previous_address_one,
        created,
      ),
      ...formatAddress(
        introduction?.consumer_details?.customer_details?.previous_address_two,
        created,
      ),
    ],
  };
};

export const formatBankRest = (introduction: Introduction) => {
  const months = introduction?.consumer_details?.bank_account?.months_held;
  if (!months) return {};
  const monthsDiff = months % 12;
  const yearsDiff = Math.floor(months / 12);
  return {
    bank_account: {
      years_owned: yearsDiff,
      months_owned: monthsDiff,
    },
  };
};

export const formatIntroductionRest = (
  introduction: Introduction,
  isExampleJob: boolean,
): ApplyFormRestFieldValues => ({
  ...formatMonthlyMortgage(introduction),
  ...formatMajorMonthlies(introduction),
  ...formatAddresses(introduction),
  ...formatBankRest(introduction),
  ...(isExampleJob
    ? {
        bankruptcy_iva: true,
        confirm_account: true,
      }
    : {}),
});

const formatIntroductionDate = (date: string | undefined): string => {
  if (!date) return '';
  return date.split('-').filter(Boolean).reverse().join('/');
};

const formatIncomes = (incomes?: Income[]) => {
  if (!incomes) return {};
  const other = incomes.filter(
    (income) => income.income_type !== 'earning_after_tax',
  );
  if (other.length === 0) return {};
  return {
    incomes: other,
  };
};

const formatExpenditures = (expenditures?: Expenditure[]) => {
  if (!expenditures) return {};
  const nonRent = expenditures.filter(
    (expenditure) =>
      !['mortgage', 'rent'].includes(expenditure.expenditure_type || ''),
  );
  if (nonRent.length === 0) return {};
  const cardPayments = nonRent.filter(
    (expenditure) => expenditure.expenditure_type === 'credit_card',
  );
  const other = nonRent.filter(
    (expenditure) => expenditure.expenditure_type !== 'credit_card',
  );
  const combined = [...cardPayments.slice(1), ...other];
  if (combined.length === 0) return {};
  return {
    expenditures: combined,
  };
};

const formatEmploymentDatePlaceholder = (months: number) => {
  const monthsDiff = months % 12;
  const yearsDiff = Math.floor(months / 12);
  return {
    months: monthsDiff,
    years: yearsDiff,
  };
};

const formatEmploymentDetails = (
  introduction: Introduction,
  isExampleJob: boolean,
) => {
  const created = introduction?.metadata?.created_at;
  const employment = introduction?.consumer_details?.employment_details;
  if (!employment || !created) return {};
  if (employment?.employed_details) {
    const placeholder = formatEmploymentDatePlaceholder(
      employment.employed_details.months_employed,
    );
    const details = {
      employed_details: {
        ...employment.employed_details,
        placeholder,
        ...(isExampleJob ? { employer_address: EXAMPLE_ADDRESS } : {}),
      },
    };
    return details;
  }
  if (employment?.self_employed_details) {
    const placeholder = formatEmploymentDatePlaceholder(
      employment.self_employed_details.months_self_employed,
    );
    return {
      self_employed_details: {
        ...employment.self_employed_details,
        placeholder,
        ...(isExampleJob ? { business_address: EXAMPLE_ADDRESS } : {}),
      },
    };
  }
  if (employment?.not_employed_details) {
    const placeholder = formatEmploymentDatePlaceholder(
      employment.not_employed_details.months_not_employed,
    );
    return {
      not_employed_details: {
        ...employment.not_employed_details,
        placeholder,
      },
    };
  }
  if (employment?.retired_details) {
    const placeholder = formatEmploymentDatePlaceholder(
      employment.retired_details.months_retired,
    );
    return {
      retired_details: {
        ...employment.retired_details,
        placeholder,
      },
    };
  }
  return {};
};

const formatIntoductionToCredit = (
  introduction: Introduction,
  isExampleJob: boolean,
): RecursivePartial<Credit> => ({
  ...introduction.consumer_details,
  customer_details: {
    ...introduction.consumer_details?.customer_details,
    date_of_birth: formatIntroductionDate(
      introduction.consumer_details?.customer_details.date_of_birth,
    ),
    ...(isExampleJob ? { middle_name: 'accept' } : {}),
  },
  finance_details: {
    bankrupcy_or_iva_in_last_5_years:
      introduction.consumer_details?.finance_details
        ?.bankrupcy_or_iva_in_last_5_years,
    ...formatIncomes(introduction.consumer_details?.finance_details?.incomes),
    ...formatExpenditures(
      introduction.consumer_details?.finance_details?.expenditures,
    ),
  },
  employment_details: {
    ...introduction?.consumer_details?.employment_details,
    ...formatEmploymentDetails(introduction, isExampleJob),
  },
});

export const formatIntroductionData = (
  introduction: Introduction,
  isExampleJob: boolean,
): ApplyFormFieldValues => {
  const credit = formatIntoductionToCredit(
    introduction,
    isExampleJob,
  ) as ApplyFormCreditFieldValues;
  const rest = formatIntroductionRest(introduction, isExampleJob);
  return {
    credit,
    ...rest,
  };
};

export const formatCustomerDetails = (
  job: Job,
): Partial<CustomerDetails> | undefined => {
  const { customer } = job;
  if (!customer) return undefined;
  const { address, phone, ...rest } = customer;
  return {
    mobile: phone,
    ...rest,
  };
};

export const formatCreditFromJob = (job: Job): RecursivePartial<Credit> => ({
  customer_details: formatCustomerDetails(job),
});

export const formatExampleRest = (
  rest: ApplyFormRestFieldValues,
): ApplyFormRestFieldValues => ({
  ...rest,
  bank_account: {
    years_owned: 10,
    months_owned: 0,
  },
  address: [
    {
      year_moved_in: '2010',
      month_moved_in: '5',
    },
  ],
});

export const formatExampleCredit = (
  credit: RecursivePartial<Credit>,
): RecursivePartial<Credit> => {
  const splitDate = (credit?.customer_details?.date_of_birth || '01-01-1990')
    .split('-')
    .filter(Boolean);

  return {
    ...credit,
    employment_details: {
      ...credit.employment_details,
      employed_details: {
        ...((credit.employment_details || {}).employed_details || {}),
        placeholder: {
          years: 10,
          months: 0,
        },
      } as Credit['employment_details']['employed_details'],
    },
    customer_details: {
      ...credit.customer_details,
      date_of_birth: [splitDate[2], splitDate[1], splitDate[0]].join('/'),
    },
  };
};

export const formatBaseData = (
  job: Job,
  isExampleJob: boolean,
): ApplyFormFieldValues => {
  const credit = formatCreditFromJob(job);
  if (isExampleJob) {
    const rest = formatExampleRest({});
    return {
      credit: formatExampleCredit(credit),
      ...rest,
    } as ApplyFormFieldValues;
  }
  return {
    credit,
  } as ApplyFormFieldValues;
};

export const formatCreditInfo = (
  job: Job | undefined,
  introduction: Introduction | undefined,
  isExampleJob: boolean,
  isIntroduced: boolean,
  cache: ExtendedCache | undefined,
  cacheKey: string | undefined,
): ApplyFormFieldValues | undefined => {
  const cacheString = cache && cacheKey ? cache?.[cacheKey] : undefined;
  if (cacheString && !isExampleJob && !isIntroduced) {
    return safeJsonParse<ApplyFormFieldValues>(cacheString);
  }
  if (isIntroduced && introduction)
    return formatIntroductionData(introduction, isExampleJob);
  if (!job) return undefined;
  return formatBaseData(job, isExampleJob);
};
